import React, { useCallback, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { OrbitProgress } from "react-loading-indicators";

import { UseShopControllersData } from "../../../services/API/ShopAPI/GetShopControllers.jsx";
import MarketingMenu from "../ControllerMarketingMenu.jsx";

import { HARDWARE_TYPE_MAP, GET_CONTROLLER_STATUS, } from "../../constants.js";

const getHardwareLabel = (hardware_v) =>
  HARDWARE_TYPE_MAP?.[Number(hardware_v)] ??
  (hardware_v != null ? `Unknown (${hardware_v})` : "-");

/**
 * Returns the safest available device name.
 */
const getSafeDeviceName = (controller) => {
  return (
    controller?.device_info?.ssid ??
    controller?.ssid ??
    controller?.name ??
    "Device name not updated"
  );
};

/**
 * Checks whether the device name is missing.
 */
const isDeviceNameOutdated = (controller) => {
  return !controller?.device_info?.ssid;
};

/**
 * Returns the safest available firmware version.
 */
const getSafeFirmwareVersion = (controller) => {
  return (
    controller?.device_info?.firmware_v ??
    controller?.device_info?.firmwareVersion ??
    controller?.firmware_v ??
    controller?.firmwareVersion ??
    null
  );
};

/**
 * Checks whether the firmware version is missing.
 */
const isFirmwareOutdated = (controller) => {
  return !getSafeFirmwareVersion(controller);
};



const SimpleShopDetails = ({ isOpen, shop, onClose }) => {
  // const shopId = shop?.id || "";
  const shopId = shop?.id ?? "";

  // -------------------------------------
  // Controller details popup state
  // -------------------------------------
  const [isMarketingMenuOpen, setMarketingMenuOpen] = useState(false);
  const [selectedController, setSelectedController] = useState(null);
  const [deviceSearch, setDeviceSearch] = useState("");

  const openControllerDetails = useCallback((controller) => {
    setSelectedController(controller);
    setMarketingMenuOpen(true);
  }, []);

  const closeControllerDetails = () => {
    setMarketingMenuOpen(false);
    setSelectedController(null);
  };

  const closeEverything = () => {
    closeControllerDetails();
    onClose();
  };

  // -------------------------------------
  // Fetch controllers only from selected shop
  // -------------------------------------
  const {
    data: shopControllerData,
    isPending,
    error,
  } = UseShopControllersData(shopId, {
    enabled: isOpen && !!shopId,
  });

  // -------------------------------------
  // Controllers list
  // -------------------------------------
  const controllers = useMemo(() => {
    const list = Array.isArray(shopControllerData?.controllerDetails)
      ? shopControllerData.controllerDetails
      : [];

    const search = deviceSearch
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

    if (!search) {
      return list;
    }

    return list.filter((controller) => {
      const deviceId = String(controller.id ?? controller.device_id ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

      return deviceId.includes(search);
    });
  }, [shopControllerData, deviceSearch]);

  const controllerStats = useMemo(() => {
  const list = Array.isArray(shopControllerData?.controllerDetails)
    ? shopControllerData.controllerDetails
    : [];

  const online = list.filter(
    (controller) => GET_CONTROLLER_STATUS(controller) === "Online"
  ).length;

  return {
    total: list.length,
    online,
    offline: list.length - online,
  };
}, [shopControllerData]);

  // -------------------------------------
  // Table columns
  // -------------------------------------
  const columns = useMemo(
    () => [
      {
        header: "Device Name",
        id: "deviceName",
        accessorFn: (row) => getSafeDeviceName(row),
        cell: ({ row, getValue }) => {
          const ssid = getValue();
          const id = row.original?.id ?? row.original?.device_id ?? "-";
          const deviceNameOutdated = isDeviceNameOutdated(row.original);

          return (
            <div className="flex flex-col items-le">
              {/* Display device name and status on the same row. */}
              <div className="flex items-center justify-start gap-2">
                <span className="text-sm font-medium text-stone-800">
                  {ssid}
                </span>

                {deviceNameOutdated && (
                  <span className="inline-flex items-center rounded-md border border-amber-200 bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-700 whitespace-nowrap">
                    Old info
                  </span>
                )}
              </div>

              {/* Display the controller ID below the device name. */}
              <div className="text-xs text-left text-stone-500 break-all">{id}</div>
            </div>
          );
        },
      },
      {
        header: "Hardware Type",
        id: "hardwareType",
        accessorFn: (row) =>
          getHardwareLabel(row?.device_info?.hardware_v ?? row?.hardware_v),
        cell: (info) => info.getValue(),
      },

      {
        header: "Firmware Version",
        id: "firmwareVersion",
        accessorFn: (row) => getSafeFirmwareVersion(row),

        cell: ({ row, getValue }) => {
          const firmwareVersion = getValue();
          const firmwareOutdated = isFirmwareOutdated(row.original);

          return (
            <div className="flex w-full items-center justify-center gap-2">
              {/* Display firmware version and status centered in the column. */}
              <span className="text-sm font-medium text-stone-700">
                {firmwareVersion ?? "Not updated"}
              </span>

              {firmwareOutdated && (
                <span className="inline-flex items-center rounded-md border border-amber-200 bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-700 whitespace-nowrap">
                  Old firmware
                </span>
              )}
            </div>
          );
        },
      },

      {
        header: "Actions",
        id: "actions",
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => openControllerDetails(row.original)}
            className="inline-flex items-center justify-center rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700 shadow-sm transition-colors hover:bg-stone-50"
          >
            Log Details
          </button>
        ),
      },

      {
        header: "Status",
        id: "status",

        cell: ({ row }) => {
          const status = GET_CONTROLLER_STATUS(row.original);

          const statusClasses =
            status === "Online"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-slate-300 bg-slate-100 text-slate-600";

          return (
            <span
              className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${statusClasses}`}
            >
              {status}
            </span>
          );
        },
      },
    ],
    [openControllerDetails],
  );

  // -------------------------------------
  // React Table instance
  // -------------------------------------
  const table = useReactTable({
    data: controllers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const clearSearch = () => {
    setDeviceSearch("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={closeEverything} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b border-stone-200 p-5 shrink-0">
          <h2 className="text-2xl font-bold text-stone-900 truncate">
            {shop?.name ?? "Shop Details"}
          </h2>

          {/* Shop information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <p className="text-xs text-stone-500 uppercase">Brand</p>
              <p className="font-semibold text-stone-900 truncate">
                {shop?.brand ?? "-"}
              </p>
            </div>

            <div>
              <p className="text-xs text-stone-500 uppercase">Project</p>
              <p className="font-semibold text-stone-900 truncate">
                {shop?.projectNumber ?? "-"}
              </p>
            </div>

            <div>
              <p className="text-xs text-stone-500 uppercase">Email</p>
              <p className="font-semibold text-stone-900 truncate">
                {shop?.user_email ?? "-"}
              </p>
            </div>
          </div>
        </div>

        
{/* Controllers summary */}
<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5 mx-5">
  <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-center">
    <p className="text-xs font-semibold uppercase text-stone-500">
      Total Controllers
    </p>
    <p className="mt-1 text-2xl font-bold text-stone-900">
      {isPending ? "..." : controllerStats.total}
    </p>
  </div>

  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center">
    <p className="text-xs font-semibold uppercase text-emerald-700">
      Online
    </p>
    <p className="mt-1 text-2xl font-bold text-emerald-700">
      {isPending ? "..." : controllerStats.online}
    </p>
  </div>

  <div className="rounded-lg border border-slate-200 bg-slate-100 p-4 text-center">
    <p className="text-xs font-semibold uppercase text-slate-600">
      Offline
    </p>
    <p className="mt-1 text-2xl font-bold text-slate-700">
      {isPending ? "..." : controllerStats.offline}
    </p>
  </div>
</div>


        <div className="mt-4 px-5">
          <label className="block text-xs text-stone-500 uppercase mb-1">
            Search Device ID
          </label>

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={deviceSearch}
              onChange={(e) => setDeviceSearch(e.target.value)}
              placeholder="Enter Device ID..."
              className="w-full md:w-80 rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <button
              type="button"
              onClick={clearSearch}
              disabled={!deviceSearch}
              className="px-3 py-2 rounded-lg border border-stone-300 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Controllers table */}
        <div className="p-5 flex-1 overflow-hidden">
          {isPending ? (
            <div className="flex justify-center py-10">
              <OrbitProgress color="#444444" size="medium" />
            </div>
          ) : error ? (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 font-medium">
              Error loading controllers.
            </div>
          ) : (
            <div className="border border-stone-200 rounded-lg overflow-auto max-h-[55vh]">
              <table className="min-w-full text-sm">
                {/* Table header */}
                <thead className="bg-stone-100 sticky top-0 z-10">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-4 py-3 text-xs font-bold uppercase text-stone-700 whitespace-nowrap text-center"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                {/* Table body */}
                <tbody className="bg-white divide-y divide-stone-200">
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-4 py-8 text-center text-sm text-stone-500"
                      >
                        No controllers found.
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="hover:bg-stone-50">
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="px-4 py-3 text-sm whitespace-nowrap text-center"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 flex justify-end shrink-0">
          <button
            type="button"
            onClick={closeEverything}
            className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 text-sm font-semibold"
          >
            Close
          </button>
        </div>
      </div>

      {/* Controller details popup */}
      <MarketingMenu
        isOpen={isMarketingMenuOpen}
        controller={selectedController}
        onClose={closeControllerDetails}
        onCloseAll={closeEverything}
      />
    </div>
  );
};

export default SimpleShopDetails;
